import React, { useState, useEffect, useMemo } from "react";
import Gauge from "./gauge";
import InstanceTable from "./instanceTable";

import {
  CardContainer,
  CardHeader,
  HName,
  ToggleHint,
  Arrow,
  AvailWrap,
  AvailChip,
  DesktopGrid,
  MobileTopRow,
  MobileGpuRow,
} from "./styles";

import { hypervisorAPI } from "api";
import { getTaskData } from "api/taskStatus";

const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

function listGpuPerDevice(gpu) {
  if (!gpu) return [];
  return Object.entries(gpu)
    .map(([id, g]: [any, any]) => ({
      id,
      profile: g.profile,
      used: g.used,
      total: g.total,
    }))
    .sort((a, b) => a.profile - b.profile);
}

const colorForProfile = (q) =>
({
  8: "#38bdf8",
  12: "#14b8a6",
  16: "#8b5cf6",
  24: "#f59e0b",
  48: "#fb7185",
}[q] || "#94a3b8");

const formatGBPairString = (u, t) => {
  if (u >= 1024 && t >= 1024) {
    return {
      text: `${(u / 1024).toFixed(2)} / ${(t / 1024).toFixed(2)}`,
      unit: "TB",
    };
  }
  return { text: `${u} / ${t}`, unit: "GB" };
};

const Card = ({ name, index, onStatusChange }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  const hv = data;

  const hasInstances = useMemo(() => {
    return Array.isArray(hv?.instances) && hv.instances.length > 0;
  }, [hv]);

  const cpu = useMemo(() => hv?.cpu ?? { used: 0, total: 0 }, [hv]);
  const ram = useMemo(() => hv?.ram ?? { used: 0, total: 0 }, [hv]);
  const ramFmt = useMemo(() => formatGBPairString(ram.used, ram.total), [ram]);

  const gpuDevices = useMemo(() => listGpuPerDevice(hv?.gpu), [hv]);
  const top3Gpu = useMemo(() => gpuDevices.slice(0, 3), [gpuDevices]);

  const availEntries = useMemo(() => {
    const obj = hv?.available_gpu ?? {};
    return Object.entries(obj).sort((a, b) => Number(b[0]) - Number(a[0]));
  }, [hv]);

  useEffect(() => {
    let alive = true;
    onStatusChange(name, "PENDING");

    (async () => {
      try {
        const poll = await getTaskData(() =>
          hypervisorAPI.getHypervisorHostname(name)
        );

        if (!alive) return;
        if (!poll) {
          onStatusChange(name, "FAIL");
          return;
        }

        const st = poll.status;

        if (st === "SUCCESS") {
          setData(poll.result);
          onStatusChange(name, "SUCCESS");
        } else {
          onStatusChange(name, "FAIL");
        }
      } catch (e) {
        if (alive) onStatusChange(name, "FAIL");
      }
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name]);

  if (!data) return null;

  const toggle = () => hasInstances && setOpen((v) => !v);

  return (
    <CardContainer
      onClick={toggle}
      $open={open}
      $clickable={hasInstances}
      className={"fade-in"}
    >
      <ToggleHint $open={open} $visible={hasInstances}>
        Instances {hv.instances?.length}
        <Arrow $open={open} />
      </ToggleHint>

      <CardHeader>
        <HName>{name}</HName>
        {availEntries.length > 0 && (
          <AvailWrap>
            {availEntries.map(([q, cnt]) => (
              <AvailChip key={q} $color={colorForProfile(Number(q))}>
                <span className="dot" />
                {q} × {cnt}
              </AvailChip>
            ))}
          </AvailWrap>
        )}
      </CardHeader>

      <DesktopGrid>
        <Gauge title="CPU" used={cpu.used} total={cpu.total} unit="Core" />
        <Gauge
          title="RAM"
          used={ram.used}
          total={ram.total}
          unit={ramFmt.unit}
          format={() => ramFmt.text}
        />
        {top3Gpu.map((g) => (
          <Gauge
            title={`${g.profile}Q`}
            used={g.used}
            total={g.total}
            unit="Q"
            color={colorForProfile(g.profile)}
          />
        ))}
      </DesktopGrid>

      <MobileTopRow>
        <Gauge title="CPU" used={cpu.used} total={cpu.total} unit="Core" />
        <Gauge
          title="RAM"
          used={ram.used}
          total={ram.total}
          unit={ramFmt.unit}
          format={() => ramFmt.text}
        />
      </MobileTopRow>

      {gpuDevices.length > 0 && (
        <MobileGpuRow>
          {gpuDevices.map((g) => (
            <Gauge
              title={`${g.profile}Q`}
              used={g.used}
              total={g.total}
              unit="Q"
              color={colorForProfile(g.profile)}
            />
          ))}
        </MobileGpuRow>
      )}

      {hasInstances && <InstanceTable open={open} instances={hv.instances} />}
    </CardContainer>
  );
};

export default Card;
